import React, { useState, useEffect, useRef, useCallback } from "react";
import { MagnifyingGlass, ArrowRight, Plus, Spinner } from "@phosphor-icons/react";
import { searchSymbols } from "../lib/api";

const TYPE_COLOR = {
  stocks: "#FFCC00",
  indices: "#34C759",
  crypto: "#A1A1AA",
  forex: "#FF9F0A",
  commodities: "#FF3B30",
  bonds: "#5AC8FA",
  other: "#A1A1AA",
};

export default function SymbolSearch({
  onAdd,
  onSelect,
  placeholder = "Search any ticker · e.g. RELIANCE, AAPL, BTC",
  testid = "symbol-search",
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await searchSymbols(q.trim(), 8);
        setResults(r.results || []);
        setOpen(true);
        setActive(-1);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = useCallback(
    (row) => {
      if (!row) return;
      onAdd && onAdd(row.symbol);
      setQ("");
      setOpen(false);
    },
    [onAdd]
  );

  const view = useCallback(
    (row) => {
      if (!row) return;
      onSelect && onSelect(row.symbol);
      setQ("");
      setOpen(false);
    },
    [onSelect]
  );

  const onKey = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter" && q.trim() && results.length === 0 && !loading) {
        // No suggestions found — surface a friendly "no results" cue rather than
        // silently adding an unknown symbol.
        return;
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a <= 0 ? results.length - 1 : a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = active >= 0 ? active : 0;
      choose(results[idx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      <div className="flex items-center bg-black border border-white/10 focus-within:border-white/30 transition-colors duration-150">
        <MagnifyingGlass size={14} weight="bold" className="ml-3 text-white/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="flex-1 bg-transparent px-3 py-2 mono text-sm focus:outline-none placeholder:text-white/30"
          data-testid={`${testid}-input`}
          autoComplete="off"
          spellCheck={false}
        />
        {loading && <Spinner size={14} className="mr-3 text-white/40 animate-spin" />}
      </div>

      {open && results.length === 0 && !loading && q.trim().length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-surface2 border border-white/15 px-3 py-3 overline text-center"
          data-testid={`${testid}-empty`}
        >
          no matches · try a fuller symbol or company name
        </div>
      )}

      {open && results.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-surface2 border border-white/15 max-h-80 overflow-y-auto shadow-xl"
          data-testid={`${testid}-dropdown`}
        >
          {results.map((r, i) => (
            <div
              key={`${r.symbol}-${i}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(r)}
              className={`flex items-center gap-2 px-3 py-2 border-b border-white/5 cursor-pointer transition-colors duration-100 ${
                active === i ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"
              }`}
              data-testid={`${testid}-row-${r.symbol}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="mono text-sm text-white truncate">{r.symbol}</span>
                  <span
                    className="mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5"
                    style={{
                      color: TYPE_COLOR[r.type] || TYPE_COLOR.other,
                      border: `1px solid ${TYPE_COLOR[r.type] || TYPE_COLOR.other}33`,
                    }}
                  >
                    {r.type}
                  </span>
                  {r.in_universe && (
                    <span className="mono text-[9px] uppercase tracking-[0.15em] text-bullish">★</span>
                  )}
                </div>
                <div className="mono text-[10px] text-white/50 truncate mt-0.5">
                  {r.name} · {r.exchange}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  view(r);
                }}
                className="text-white/40 hover:text-white p-1"
                title="Open detail"
                data-testid={`${testid}-view-${r.symbol}`}
              >
                <ArrowRight size={14} weight="bold" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  choose(r);
                }}
                className="bg-white/5 hover:bg-white/15 text-white p-1"
                title="Add to watchlist"
                data-testid={`${testid}-add-${r.symbol}`}
              >
                <Plus size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
